import { getIdentifier } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { HEALTH_SAMPLE_PLATFORM_NAMES, parseHealthSamplePlatform } from './healthSample';
import { IDENTIFIER_SYSTEMS } from './identifiers';
import { HG_OBSERVATION_GROUP_PROFILE, isImportedObservation } from './resultRecords';
import { RESULT_CODE_SYSTEMS } from './taskUtils';

/**
 * Where a reading came from, at the resolution every surface needs:
 *
 * - `device` — synced from the patient's phone health store.
 * - `clinical` — resulted by a lab or filed by a health-record import. Deliberately not `lab`: an
 *   EHR-imported blood pressure is not a lab result, and both read to a patient as "from your
 *   health records".
 * - `entered` — typed in by hand, by the patient or a clinician.
 *
 * There is no `unknown`: a reading with none of the signals below was keyed in, which is exactly what
 * `entered` means.
 */
export type ObservationOrigin = 'device' | 'clinical' | 'entered';

/**
 * A reading's provenance. `source` is a proper noun — "Apple Health", "Quest Diagnostics", a
 * performer's display — which hosts render as written; there is nothing to translate in a name. A
 * host with no `source` translates the `origin` instead, through its own literal `t()` keys.
 */
export type ObservationProvenance = {
  origin: ObservationOrigin;
  source?: string;
};

const LIZA_SYSTEM_FRAGMENT = 'liza.health';

/** A coding from a system that is neither a result code system nor one Liza mints — i.e. a source
 * system's own code, which only an import carries. */
const hasForeignCoding = (observation: Observation): boolean =>
  (observation.code?.coding ?? []).some(
    (coding) =>
      !!coding.system && !RESULT_CODE_SYSTEMS.includes(coding.system) && !coding.system.includes(LIZA_SYSTEM_FRAGMENT),
  );

const referencesDocument = (observation: Observation): boolean =>
  (observation.derivedFrom ?? []).some((ref) => ref.reference?.startsWith('DocumentReference/'));

/**
 * Resolve where a reading came from, from the resource alone.
 *
 * A device sync is unambiguous — it carries the platform-namespaced `HEALTH_SAMPLE` identifier the
 * sync writes — so it is checked first. Everything clinical is then recognised by any one of the
 * marks a resulted or imported reading carries: a Health Gorilla group profile, the external-source
 * identifier an import stamps, a named `performer`, a coding from the source system's own vocabulary,
 * a `meta.source`, or a `derivedFrom` pointing at the report it came in on. Anything left is a value
 * somebody typed.
 *
 * Modelling the app or device that produced a reading is #6125; this descriptor is the shape that
 * work enriches, so callers written against it do not change.
 */
export const resolveObservationProvenance = (observation: Observation): ObservationProvenance => {
  const platform = parseHealthSamplePlatform(getIdentifier(observation, IDENTIFIER_SYSTEMS.HEALTH_SAMPLE));
  if (platform) return { origin: 'device', source: HEALTH_SAMPLE_PLATFORM_NAMES[platform] };

  const isClinical =
    observation.meta?.profile?.includes(HG_OBSERVATION_GROUP_PROFILE) ||
    isImportedObservation(observation) ||
    (observation.performer?.length ?? 0) > 0 ||
    hasForeignCoding(observation) ||
    !!observation.meta?.source ||
    referencesDocument(observation);

  if (isClinical) return { origin: 'clinical', source: observation.performer?.[0]?.display };
  return { origin: 'entered' };
};
